import { CalendarType } from 'components/CruiseSearchWidget/types';
import { getDay, isWithinInterval, parseISO } from 'date-fns';
import { DateRangeFilter, DateRangeFilterMonth } from 'lib/apiClient';
import { DatesCalendarType, DayType, MonthType, YearType } from '../DatesCalendar.types';

interface GetDayDisabledStateProps {
	date: string;
	isMonthDisabled: boolean;
	criteriaDateRanges: Array<string>;
}

function setAndGetSelectedDays(defaultCalendar: DatesCalendarType, rangeSelection: string[]) {
	const updatedCalendar: DatesCalendarType = JSON.parse(JSON.stringify(defaultCalendar));

	const [startDate, endDate] = rangeSelection;

	let startDateObject: DayType | undefined;
	let endDateObject: DayType | undefined;

	if (startDate) {
		const [sYear, sMonth] = startDate.split('-');
		const sMonthCode = [sYear, sMonth].join('-');
		const daySelected = updatedCalendar
			.find((year) => year.year === sYear)
			?.months.find((month) => month.code === sMonthCode)
			?.days.find((day) => day?.code === startDate);
		if (daySelected) {
			daySelected.isSelected = true;
			startDateObject = daySelected;
		}
	}

	if (endDate) {
		const [eYear, eMonth] = endDate.split('-');
		const eMonthCode = [eYear, eMonth].join('-');
		const daySelected = updatedCalendar
			.find((year) => year.year === eYear)
			?.months.find((month) => month.code === eMonthCode)
			?.days.find((day) => day?.code === endDate);
		if (daySelected) {
			daySelected.isSelected = true;
			endDateObject = daySelected;
		}
	}

	return { updatedCalendar, startDateObject, endDateObject };
}

export function updateCalendarSelection(defaultCalendar: DatesCalendarType, datesSelection: Array<DayType>): DatesCalendarType {
	if (datesSelection.length === 0) return defaultCalendar;
	const [startDate, endDate] = datesSelection;
	const stringDatesRange = [startDate?.code, endDate?.code];
	const { updatedCalendar } = setAndGetSelectedDays(defaultCalendar, stringDatesRange);
	return updatedCalendar;
}

export function getInitialSelection(defaultCalendar: DatesCalendarType, initialSelection: string) {
	const response = setAndGetSelectedDays(defaultCalendar, initialSelection.split('~'));
	return {
		updatedCalendar: response.updatedCalendar,
		startSelectionDate: response.startDateObject,
		endSelectionDate: response.endDateObject,
	};
}

export function formatCalendar(calendar: DatesCalendarType): DatesCalendarType {
	const formattedCalendar: DatesCalendarType = JSON.parse(JSON.stringify(calendar));

	formattedCalendar.forEach((year: YearType) =>
		year.months.forEach((month: MonthType) => {
			if (month.days[0] === null) return;

			const newMonth = { ...month };

			const numberOfFirstDay = getDay(new Date(month.days[0].code + 'T00:00:00.000')); // 0
			const emptyArray = new Array(numberOfFirstDay).fill(null);
			newMonth.days.unshift(...emptyArray);
		}),
	);

	return formattedCalendar;
}

export function getCalendar(
	startDate: string,
	endDate: string,
	cruiseDatesFilter: DateRangeFilter,
	language: string,
	criteriaDateRanges: Array<string> = [],
): DatesCalendarType {
	const start = new Date(`${startDate}T10:00:00`);
	const end = new Date(`${endDate}T10:00:00`);
	const years: DatesCalendarType = [];

	while (start <= end) {
		const year = start.getFullYear().toString();
		const monthName = start.toLocaleString(language, { month: 'long' });
		const dayOfWeek = start.toLocaleString(language, { weekday: 'short' });

		let month: string = (start.getMonth() + 1).toString();
		month = Number(month) < 10 ? `0${month}` : month;
		let dayString: string = start.getDate().toString();
		dayString = Number(dayString) < 10 ? `0${dayString}` : dayString;
		const monthCode = `${year}-${month}`;
		const dayCode = `${monthCode}-${dayString}`;

		let monthFacet = {} as DateRangeFilterMonth;
		cruiseDatesFilter.years.forEach((yearFacet) => {
			if (yearFacet.code === year && yearFacet.months.find((month) => month.code === monthCode)) {
				monthFacet = yearFacet.months.find((month) => month.code === monthCode) as DateRangeFilterMonth;
			}
		});

		const day: DayType = {
			code: dayCode,
			date: start.getDate().toString(),
			year: year.toString(),
			isSelected: false,
			isDisabled: getDayDisabledState({ date: dayCode, isMonthDisabled: monthFacet.isDisabled, criteriaDateRanges }),
			dayOfWeek,
		};

		const existingYear = years.find((yearObj) => yearObj.year === year);

		if (existingYear) {
			const existingMonth = existingYear.months.find((month: MonthType) => month.label === monthName);

			if (existingMonth) {
				existingMonth.days.push(day);
				existingMonth.end = day.code;
			} else {
				existingYear.months.push({
					key: CalendarType.Dates,
					code: monthCode,
					label: monthName,
					days: [day],
					start: day.code,
					end: day.code,
					isDisabled: monthFacet.isDisabled,
				});
			}

			existingYear.end = day.code;
		} else {
			years.push({
				year,
				months: [
					{
						key: CalendarType.Dates,
						code: monthCode,
						label: monthName,
						days: [day],
						start: day.code,
						end: day.code,
						isDisabled: monthFacet.isDisabled,
					},
				],
				start: day.code,
				end: day.code,
			});
		}

		start.setDate(start.getDate() + 1);
	}

	return years;
}

export const getWeekDays = (language: string) => {
	const weekDays = [];
	const formatter = new Intl.DateTimeFormat(language, { weekday: 'long' });
	const baseDate = new Date(2023, 0, 1);

	for (let index = 0; index < 7; index++) {
		baseDate.setDate(baseDate.getDate() + (index > 0 ? 1 : 0));
		weekDays.push({
			label: formatter.format(baseDate),
			code: language === 'zh' ? formatter.format(baseDate).slice(-1).toUpperCase() : formatter.format(baseDate).substring(0, 1).toUpperCase(),
		});
	}

	return weekDays;
};

const getDayDisabledState = ({ date, isMonthDisabled, criteriaDateRanges = [] }: GetDayDisabledStateProps) => {
	if (criteriaDateRanges.length === 0) {
		return isMonthDisabled;
	}

	const dateDay = parseISO(date);

	const isValidDate = criteriaDateRanges.some((criteriaDateRange) => {
		const [criteriaStartDate, criteriaEndDate] = criteriaDateRange.split('~');
		const innerRangeStart = parseISO(criteriaStartDate);
		const innerRangeEnd = parseISO(criteriaEndDate);

		return isWithinInterval(dateDay, { start: innerRangeStart, end: innerRangeEnd });
	});

	return !isValidDate;
};
